import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import supplierService from '../../services/supplierService';
import productService from '../../services/productService';
import categoryService from '../../services/categoryService';
import useAvailableStock from '../../hooks/useAvailableStock';
import SupplierArticleItem from './SupplierArticleItem';
import '../Reception/SimpleReception.css';

const purchasePrice = (product) => Math.round(Number(product.prixAchatTTC || 0) / (1 + Number(product.tauxTVAAchat ?? 0.2)) * 100) / 100;
const asLink = (product) => ({ articleId: product.id, articleLibelle: product.libelle,
    codeBarre: product.codeBarre, unite: product.unite, tauxTVAAchat: product.tauxTVAAchat,
    prixAchatHT: purchasePrice(product) });
const message = (error) => error?.response?.data?.message || error?.message || 'Enregistrement impossible';

// A controlled catalogue is saved with the supplier; a document catalogue saves links immediately.
export default function SupplierArticles({ supplierId, articles, onChange, onPick, selectedIds = [], disabled = false, refreshKey = 0, onStockRefresh,
    documentItems, quantityField, onItemChange, onItemRemove }) {
    const managed = Boolean(onChange);
    const [loaded, setLoaded] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const [query, setQuery] = useState('');
    const [candidate, setCandidate] = useState(null);
    const [price, setPrice] = useState(0);
    const [showCreate, setShowCreate] = useState(false);
    const [categories, setCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [draft, setDraft] = useState({});
    const currentSupplier = useRef(supplierId);
    currentSupplier.current = supplierId;
    const rows = managed ? (articles || []) : [...loaded, ...(documentItems || []).filter(item => item.articleId && !loaded.some(row => String(row.articleId) === String(item.articleId)))
        .map(item => ({ articleId: item.articleId, articleLibelle: item.articleNom, prixAchatHT: item.prixUnitaireHT, tauxTVAAchat: item.tauxTVA, unite: item.unite }))];
    const availability = useAvailableStock(onPick ? rows.map(row => row.articleId) : []);

    useEffect(() => {
        if (managed) return;
        let active = true;
        setLoaded([]);
        setCandidate(null);
        setQuery('');
        setError('');
        setLoading(Boolean(supplierId));
        if (supplierId) supplierService.articles(supplierId)
            .then(data => { if (active) setLoaded(data || []); })
            .catch(err => { if (active) setError(message(err)); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [supplierId, managed, refreshKey, reload]);

    const addLink = async (link) => {
        if (managed) {
            onChange([...rows.filter(row => String(row.articleId) !== String(link.articleId)), link]);
        } else {
            const id = supplierId;
            const saved = await supplierService.saveArticle(id, link);
            if (currentSupplier.current !== id) return;
            setLoaded(current => [...current.filter(row => String(row.articleId) !== String(link.articleId)), saved]);
        }
    };

    const attach = async () => {
        if (busy || disabled || loading) return;
        if (candidate && rows.some(row => String(row.articleId) === String(candidate.id))) {
            setError('Cet article est déjà associé au fournisseur.');
            return;
        }
        if (!candidate || price === '' || !Number.isFinite(Number(price)) || Number(price) < 0) {
            setError('Choisissez un article et indiquez son prix.');
            return;
        }
        setBusy(true); setError('');
        try {
            const supplier = supplierId;
            const ht = managed ? Number(price) : purchasePrice({ ...candidate, prixAchatTTC: price });
            await addLink({ ...asLink(candidate), prixAchatHT: ht });
            if (!managed && currentSupplier.current === supplier) onPick?.(candidate, ht);
            setCandidate(null);
        } catch (err) { setError(message(err)); }
        finally { setBusy(false); }
    };

    const openCreate = async () => {
        setError(''); setBusy(true);
        try {
            const [categoryResult, unitResult] = await Promise.all([categoryService.list(''), productService.findAllUnites()]);
            setCategories(categoryResult.data.body || []);
            setUnits(unitResult.data || []);
            setDraft({ libelle: '', codeBarre: '', categorieId: '', unite: unitResult.data?.[0]?.code || '',
                prixAchatTTC: 0, prixVenteTTC1: 0, tauxTVAAchat: 0.2 });
            setShowCreate(true);
        } catch (err) { setError(message(err)); }
        finally { setBusy(false); }
    };

    const create = async () => {
        if (busy || disabled) return;
        if (!draft.libelle?.trim() || !draft.categorieId || !draft.unite ||
            [draft.prixAchatTTC, draft.prixVenteTTC1, draft.tauxTVAAchat].some(value => value === '' || !Number.isFinite(Number(value)) || Number(value) < 0)) {
            setError('Renseignez la désignation, la catégorie, l’unité et des prix valides.');
            return;
        }
        setBusy(true); setError('');
        const id = supplierId;
        try {
            const dto = { ...draft, libelle: draft.libelle.trim(), typeArticle: 'STOCKABLE', actif: true, enStock: true,
                prixAchatTTC: Number(draft.prixAchatTTC), prixVenteTTC1: Number(draft.prixVenteTTC1),
                tauxTVAAchat: Number(draft.tauxTVAAchat), tauxTVAVente: Number(draft.tauxTVAAchat),
                prixVenteTTC2: 0, prixVenteTTC3: 0, stockMinimal: 0, stockPhysique: 0, stockDisponible: 0,
                fournisseurs: !managed && id ? [{ fournisseurId: id, prixAchatHT: purchasePrice(draft) }] : [] };
            const body = new FormData();
            body.append('article', new Blob([JSON.stringify(dto)], { type: 'application/json' }));
            const { data } = await productService.save(body);
            if (managed) onChange([...rows, asLink(data)]);
            else if (currentSupplier.current === id) {
                setLoaded(current => [...current, asLink(data)]);
                onPick?.(data, purchasePrice(data));
            }
            setShowCreate(false);
        } catch (err) { setError(message(err)); }
        finally { setBusy(false); }
    };

    if (!managed && !supplierId) return null;
    const locked = disabled || busy || loading;
    const filtered = rows.filter(row => `${row.articleLibelle} ${row.codeBarre || ''}`.toLowerCase().includes(query.toLowerCase()));
    return <section className={managed ? 'supplier-managed-catalogue' : 'supplier-catalogue'} aria-label="Articles du fournisseur">
        <div className="supplier-catalogue-heading">
            <h3>{managed ? `Articles du fournisseur (${rows.length})` : 'Cochez les articles souhaités'}</h3>
            {managed && !disabled && <Button type="button" color="primary" outline disabled={locked} onClick={openCreate}>Créer un article</Button>}
            {!managed && <Button type="button" color="link" size="sm" onClick={() => { availability.refresh(); onStockRefresh?.(); }}>Actualiser le stock</Button>}
        </div>
        {managed && <p className="supplier-form-hint">Ajoutez les articles que vous achetez chez ce fournisseur. Vous les retrouverez dans vos commandes et réceptions.</p>}
        {error && <Alert color="danger">{error} {!managed && <Button type="button" size="sm" onClick={() => setReload(value => value + 1)}>Réessayer</Button>}</Alert>}
        {availability.error && <p className="text-danger" role="alert">Le stock n’a pas pu être chargé. Cliquez sur « Actualiser le stock ».</p>}
        {loading ? <p role="status">Chargement des articles…</p> : <>
            {(rows.length > 5 || query) && <Input aria-label="Filtrer les articles du fournisseur" placeholder="Chercher un article par son nom…" value={query} onChange={event => setQuery(event.target.value)} className="mb-3" />}
            {managed ? <div className="supplier-managed-items">
                {filtered.map(row => <div className="supplier-managed-item" key={row.articleId}>
                    <div><strong>{row.articleLibelle}</strong>{row.codeBarre && <small>{row.codeBarre}</small>}</div>
                    <div><Label for={`supplier-price-${row.articleId}`}>Prix d’achat (hors taxes)</Label><Input id={`supplier-price-${row.articleId}`} aria-label={`Prix HT ${row.articleLibelle}`} type="number" min="0" step="0.01" required disabled={locked} value={row.prixAchatHT}
                        onChange={event => onChange(rows.map(item => item.articleId === row.articleId ? { ...item, prixAchatHT: event.target.value } : item))} /></div>
                    <Button type="button" size="sm" color="danger" outline disabled={locked} onClick={() => onChange(rows.filter(item => item.articleId !== row.articleId))}>Retirer</Button>
                </div>)}
            </div> : documentItems ? <div className="supplier-item-list">
                <p className="small text-muted">Cochez, puis indiquez la quantité. Les prix comprennent les taxes.</p>
                {filtered.map(row => {
                    const index = documentItems.findIndex(item => String(item.articleId) === String(row.articleId));
                    const item = documentItems[index];
                    return <SupplierArticleItem key={row.articleId} row={row} item={item} quantityField={quantityField}
                        stock={availability.quantities[row.articleId] ?? (availability.error ? 'indisponible' : undefined)} disabled={locked}
                        onSelect={() => item ? onItemRemove(index) : onPick?.({ id: row.articleId, libelle: row.articleLibelle, codeBarre: row.codeBarre, unite: row.unite, tauxTVAAchat: row.tauxTVAAchat }, Number(row.prixAchatHT))}
                        onChange={event => onItemChange(index, event)} />;
                })}
            </div> : <div className="supplier-choices">
                {filtered.map(row => {
                    const selected = selectedIds.some(id => String(id) === String(row.articleId));
                    const priceTTC = Number(row.prixAchatHT) * (1 + Number(row.tauxTVAAchat ?? 0.2));
                    return <div className={`supplier-choice ${selected ? 'is-selected' : ''}`} key={row.articleId}>
                        <div><strong>{row.articleLibelle}</strong><small>En stock : {availability.quantities[row.articleId] ?? (availability.error ? 'indisponible' : '…')}</small><small>{priceTTC.toFixed(2)} DH / unité · taxes comprises</small></div>
                        <Button type="button" color={selected ? 'success' : 'primary'} outline={selected} disabled={locked || selected} onClick={() => onPick?.({ id: row.articleId, libelle: row.articleLibelle, codeBarre: row.codeBarre, unite: row.unite, tauxTVAAchat: row.tauxTVAAchat }, Number(row.prixAchatHT))}>{selected ? '✓ Ajouté' : '+ Ajouter'}</Button>
                    </div>;
                })}
            </div>}
            {!filtered.length && <p className="text-muted mt-2 mb-0">{rows.length ? 'Aucun article trouvé.' : 'Ajoutez votre premier article ci-dessous.'}</p>}
        </>}
        {!disabled && <details className="supplier-extra" open={managed ? true : undefined}>
            <summary>+ Ajouter un autre article</summary>
            <div className="supplier-add-fields">
                <div><Label for="supplier-article-search">Nom de l’article</Label>
                    <AsyncSelect inputId="supplier-article-search" key={supplierId || 'new'} value={candidate ? { value: candidate.id, label: candidate.libelle } : null}
                        isDisabled={locked} isClearable defaultOptions placeholder="Tapez le nom de l’article…"
                        isOptionDisabled={option => rows.some(row => String(row.articleId) === String(option.value))}
                        loadOptions={async search => {
                            try {
                                const result = await productService.findReception(search, 0, 30);
                                return (result.body?.content || []).filter(product => !rows.some(row => String(row.articleId) === String(product.id)))
                                    .map(product => ({ value: product.id, label: `${product.codeBarre || ''} ${product.libelle}`, product }));
                            } catch (err) { setError(message(err)); return []; }
                        }}
                        onChange={option => { setCandidate(option?.product || null); setPrice(option ? (managed ? purchasePrice(option.product) : Number(option.product.prixAchatTTC || 0)) : 0); }} />
                </div>
                <div><Label for="supplier-article-price">{managed ? 'Prix d’achat HT' : 'Prix par unité'}</Label><Input id="supplier-article-price" type="number" min="0" step="0.01" value={price} disabled={locked} onChange={event => setPrice(event.target.value)} />{!managed && <small>Taxes comprises.</small>}</div>
                <Button type="button" color="primary" disabled={locked || !candidate} onClick={attach}>Ajouter cet article</Button>
            </div>
            {!managed && <Button type="button" color="link" className="mt-2" disabled={locked} onClick={openCreate}>L’article n’existe pas ? Créer un nouvel article</Button>}
        </details>}
        <Modal isOpen={showCreate} toggle={() => { if (!busy) setShowCreate(false); }} size="lg">
            <ModalHeader>Créer un article pour le fournisseur</ModalHeader>
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}
                {['libelle', 'codeBarre'].map((field, index) => <div className="mb-2" key={field}><Label>{['Désignation *', 'Code-barres'][index]}</Label><Input value={draft[field] || ''} disabled={busy} onChange={event => setDraft({ ...draft, [field]: event.target.value })} /></div>)}
                <Label>Catégorie *</Label><Input type="select" value={draft.categorieId || ''} disabled={busy} onChange={event => setDraft({ ...draft, categorieId: event.target.value })}><option value="">Choisir…</option>{categories.map(category => <option key={category.id} value={category.id}>{category.libelle}</option>)}</Input>
                <Label>Unité *</Label><Input type="select" value={draft.unite || ''} disabled={busy} onChange={event => setDraft({ ...draft, unite: event.target.value })}><option value="">Choisir…</option>{units.map(unit => <option key={unit.code} value={unit.code}>{unit.libelle}</option>)}</Input>
                {['prixAchatTTC', 'prixVenteTTC1'].map((field, index) => <div className="mt-2" key={field}><Label>{['Prix d’achat TTC', 'Prix de vente TTC'][index]}</Label><Input type="number" min="0" step="0.01" value={draft[field] ?? 0} disabled={busy} onChange={event => setDraft({ ...draft, [field]: event.target.value })} /></div>)}
                <Label>TVA</Label><Input type="select" value={draft.tauxTVAAchat ?? 0.2} disabled={busy} onChange={event => setDraft({ ...draft, tauxTVAAchat: event.target.value })}>{[0, 0.07, 0.1, 0.14, 0.2].map(rate => <option key={rate} value={rate}>{Math.round(rate * 100)} %</option>)}</Input>
            </ModalBody>
            <ModalFooter><Button type="button" color="primary" disabled={busy} onClick={create}>{busy ? 'Enregistrement…' : 'Créer et associer'}</Button><Button type="button" disabled={busy} onClick={() => setShowCreate(false)}>Annuler</Button></ModalFooter>
        </Modal>
    </section>;
}
